import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { CheckCircle, ArrowRight, ArrowLeft, Droplets, Utensils, Zap, Heart, Shield, Bell } from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { usePrepTestStore } from '../stores/prep-test-store';
import { areaConfig } from '@/config/area-config';
import { useNotifications } from '@/hooks/useNotifications';
import TermsOfUse from '@/components/TermsOfUse';
import { consumePostLoginRedirect } from '@/services/post-login-redirect';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:7071/api';

type OnboardingStep = 'terms' | 'welcome' | 'notifications' | 'test' | 'analyzing' | 'complete';

interface QuizQuestion {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  question: string;
  options: { value: number; label: string }[];
  area: string;
  color: string;
}

// Beredskapsfrågor baserade på Hemberedskapsskolan
const quizQuestions: QuizQuestion[] = [
  {
    id: 'water',
    icon: Droplets,
    question: 'Hur mycket dricksvatten har du lagrat hemma?',
    area: 'Vatten',
    color: areaConfig.water.color,
    options: [
      { value: 0, label: 'Inget alls' },
      { value: 25, label: 'Några flaskor (1-2 dagar)' },
      { value: 50, label: 'Dunkar för 3-4 dagar' },
      { value: 75, label: 'En veckas förråd (35L per person)' },
      { value: 100, label: 'Mer än en vecka + vattenrening' },
    ],
  },
  {
    id: 'food',
    icon: Utensils,
    question: 'Hur länge kan du klara dig med maten du har hemma?',
    area: 'Mat',
    color: areaConfig.food.color,
    options: [
      { value: 0, label: 'Mindre än 3 dagar' },
      { value: 25, label: '3-5 dagar' },
      { value: 50, label: 'Ungefär en vecka' },
      { value: 75, label: '2 veckor' },
      { value: 100, label: 'En månad eller mer' },
    ],
  },
  {
    id: 'cooking',
    icon: Utensils,
    question: 'Kan du laga varm mat utan el?',
    area: 'Mat',
    color: areaConfig.food.color,
    options: [
      { value: 0, label: 'Nej, ingen möjlighet' },
      { value: 50, label: 'Ja, jag har gasolkök eller stormkök' },
      { value: 100, label: 'Ja, och jag har övat på det' },
    ],
  },
  {
    id: 'light',
    icon: Zap,
    question: 'Har du alternativ belysning vid strömavbrott?',
    area: 'Energi',
    color: areaConfig.energy.color,
    options: [
      { value: 0, label: 'Nej, bara mobillampan' },
      { value: 25, label: 'Stearinljus' },
      { value: 50, label: 'Ficklampa med extra batterier' },
      { value: 75, label: 'Pannlampa + ficklampa + batterier' },
      { value: 100, label: 'Flera lampor + solcellsladdare' },
    ],
  },
  {
    id: 'power',
    icon: Zap,
    question: 'Kan du ladda mobilen vid långvarigt strömavbrott?',
    area: 'Energi',
    color: areaConfig.energy.color,
    options: [
      { value: 0, label: 'Nej' },
      { value: 25, label: 'Powerbank för ett par laddningar' },
      { value: 50, label: 'Stor powerbank eller billaddare' },
      { value: 100, label: 'Solcellsladdare eller större batteribank' },
    ],
  },
  {
    id: 'firstaid',
    icon: Heart,
    question: 'Hur är din första hjälpen-utrustning?',
    area: 'Hälsa',
    color: areaConfig.health.color,
    options: [
      { value: 0, label: 'Har ingen' },
      { value: 25, label: 'Några plåster' },
      { value: 50, label: 'Grundläggande förbandsväska' },
      { value: 75, label: 'Komplett första hjälpen-väska' },
      { value: 100, label: 'Komplett + tourniquet + extra mediciner' },
    ],
  },
  {
    id: 'knowledge',
    icon: Heart,
    question: 'Kan du grundläggande första hjälpen (HLR, stoppa blödning)?',
    area: 'Hälsa',
    color: areaConfig.health.color,
    options: [
      { value: 0, label: 'Nej, aldrig lärt mig' },
      { value: 25, label: 'Gick kurs för länge sedan' },
      { value: 50, label: 'Har grundläggande kunskaper' },
      { value: 100, label: 'Ja, och håller kunskaperna färska' },
    ],
  },
  {
    id: 'radio',
    icon: Shield,
    question: 'Kan du ta emot nyheter och VMA utan internet?',
    area: 'Information',
    color: areaConfig.communication.color,
    options: [
      { value: 0, label: 'Nej, bara via mobilen' },
      { value: 50, label: 'Har en radio hemma någonstans' },
      { value: 75, label: 'Har batteridriven radio redo' },
      { value: 100, label: 'Vevradio eller solcellsradio' },
    ],
  },
  {
    id: 'warmth',
    icon: Shield,
    question: 'Hur klarar du dig om värmen försvinner vintertid?',
    area: 'Skydd',
    color: areaConfig.shelter.color,
    options: [
      { value: 0, label: 'Ingen plan' },
      { value: 25, label: 'Har extra filtar' },
      { value: 50, label: 'Filtar + sovsäckar' },
      { value: 75, label: 'Reservvärme (gasol/etanolkamin)' },
      { value: 100, label: 'Braskamin eller komplett backup' },
    ],
  },
  {
    id: 'transport',
    icon: Shield,
    question: 'Har du en plan om du behöver evakuera?',
    area: 'Transport',
    color: areaConfig.transport.color,
    options: [
      { value: 0, label: 'Nej, ingen plan' },
      { value: 25, label: 'Vet vart jag skulle ta vägen' },
      { value: 50, label: 'Plan + tankat fordon' },
      { value: 75, label: 'Plan + packad evakueringsväska' },
      { value: 100, label: 'Komplett plan som familjen övat på' },
    ],
  },
];

export default function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken, user, setUser } = useAuthStore();
  const { saveTestResults } = usePrepTestStore();
  const { permissionStatus, requestPermission } = useNotifications();
  const [step, setStep] = useState<OnboardingStep>('terms');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [score, setScore] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRequestingNotifications, setIsRequestingNotifications] = useState(false);
  const [termsExpanded, setTermsExpanded] = useState(false);
  const [termsLanguage, setTermsLanguage] = useState<'sv' | 'en'>('sv');
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Detektera om användaren är på desktop (inte touch-enhet)
  const isDesktop = !('ontouchstart' in window || navigator.maxTouchPoints > 0);

  const TERMS_VERSION = '2026-03-07'; // Uppdatera när användarvillkoren ändras

  const acceptTerms = async () => {
    if (!accessToken || !termsAccepted) return;
    
    setIsSubmitting(true);
    try {
      // Log terms acceptance with version, IP, etc. (clickwrap agreement)
      const response = await fetch(`${API_BASE_URL}/users/me/accept-terms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          version: TERMS_VERSION,
          language: termsLanguage,
        }),
      });

      if (response.ok) {
        if (user) {
          setUser({ 
            ...user, 
            termsAcceptedAt: new Date().toISOString(),
            termsVersion: TERMS_VERSION,
          });
        }
        setStep(isDesktop ? 'notifications' : 'welcome');
      } else {
        console.error('Failed to accept terms:', await response.text());
        alert('Det gick inte att acceptera användarvillkoren. Försök igen.');
      }
    } catch (error) {
      console.error('Failed to accept terms:', error);
      alert('Ett fel uppstod. Kontrollera din internetanslutning och försök igen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const completeOnboarding = async (prepTestScore?: number) => {
    if (!accessToken) return;
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/me/onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          prepTestScore,
          initialSettings: {
            language: 'sv',
          },
        }),
      });

      if (response.ok) {
        if (user) {
          setUser({ ...user, onboardingCompleted: true });
        }
        const redirectPath = consumePostLoginRedirect() || '/app';
        navigate(redirectPath);
      }
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      // Fortsätt ändå för utveckling
      if (user) {
        setUser({ ...user, onboardingCompleted: true });
      }
      const redirectPath = consumePostLoginRedirect() || '/app';
      navigate(redirectPath);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartTest = () => {
    setStep('test');
    setCurrentQuestion(0);
    setAnswers({});
  };

  const handleEnableNotifications = async () => {
    setIsRequestingNotifications(true);
    try {
      await requestPermission();
    } catch (error) {
      console.error('Failed to request notification permission:', error);
    } finally {
      setIsRequestingNotifications(false);
    }
  };

  const handleContinueFromNotifications = () => {
    setStep('welcome');
  };

  const handleSkipTest = () => {
    completeOnboarding();
  };

  const handleAnswer = (value: number) => {
    const question = quizQuestions[currentQuestion];
    const newAnswers = { ...answers, [question.id]: value };
    setAnswers(newAnswers);

    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Beräkna slutpoäng
      setStep('analyzing');
      
      // Spara testresultat i store för personliga rekommendationer
      saveTestResults(newAnswers);
      
      setTimeout(() => {
        const totalScore = Object.values(newAnswers).reduce((sum, val) => sum + val, 0);
        const maxScore = quizQuestions.length * 100;
        const percentage = Math.round((totalScore / maxScore) * 100);
        setScore(percentage);
        setStep('complete');
      }, 1500);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleComplete = () => {
    completeOnboarding(score);
  };

  const getScoreLevel = (score: number) => {
    if (score >= 80) return { level: 'Avancerad hemberedskap', color: 'text-green-400', message: 'Du har en imponerande beredskap!' };
    if (score >= 60) return { level: 'God hemberedskap', color: 'text-blue-400', message: 'Du har en bra grund, men det finns utrymme för förbättring.' };
    if (score >= 40) return { level: 'Grundläggande hemberedskap', color: 'text-yellow-400', message: 'Du har kommit igång. Fortsätt bygga din beredskap steg för steg.' };
    if (score >= 20) return { level: 'Hemberedskap för nybörjare', color: 'text-orange-400', message: 'Du är i gång. Nu är det läge att börja bygga upp beredskapen på riktigt.' };
    return { level: 'Hemberedskap under uppbyggnad', color: 'text-red-400', message: 'Ingen fara, alla börjar någonstans.' };
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {step === 'terms' && (
          <div className="bg-card rounded-2xl border border-border p-8">
            <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-primary/30">
              <img src="/icons/logo.svg" alt="Beredskapshubben logga" className="h-10 w-auto" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2 text-center">
              Välkommen till Beredskapshubben!
            </h1>
            <p className="text-muted-foreground mb-6 text-center">
              Innan du börjar behöver du godkänna våra användarvillkor.
            </p>
            
            <TermsOfUse
              language={termsLanguage}
              onLanguageChange={setTermsLanguage}
              showLanguageToggle={true}
              expanded={termsExpanded}
              onToggleExpand={() => setTermsExpanded(!termsExpanded)}
              showExpandToggle={true}
              acceptButton={{
                show: false, // We use checkbox instead
                onAccept: () => {},
                disabled: false,
              }}
            />

            {/* Checkbox for accepting terms - MUST NOT be pre-checked */}
            <div className="mt-6 space-y-4">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-2 border-muted-foreground/30 
                    checked:bg-primary checked:border-primary 
                    focus:ring-2 focus:ring-primary focus:ring-offset-2 
                    transition-colors cursor-pointer"
                  disabled={isSubmitting}
                />
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  {termsLanguage === 'sv' 
                    ? 'Jag har läst och godkänner användarvillkoren' 
                    : 'I have read and accept the Terms of Use'}
                </span>
              </label>

              <button
                onClick={acceptTerms}
                disabled={!termsAccepted || isSubmitting}
                className="w-full btn-primary-custom py-3 px-4 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting 
                  ? (termsLanguage === 'sv' ? 'Behandlar...' : 'Processing...') 
                  : (termsLanguage === 'sv' ? 'Fortsätt' : 'Continue')}
              </button>
            </div>
          </div>
        )}

        {step === 'welcome' && (
          <div className="bg-card rounded-2xl border border-border p-8 text-center">
            <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-primary/30">
              <img src="/icons/logo.svg" alt="Beredskapshubben logga" className="h-10 w-auto" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              {t('onboarding.welcome.title')}
            </h1>
            <p className="text-muted-foreground mb-8">
              {t('onboarding.welcome.subtitle')}
            </p>
            <button
              onClick={handleStartTest}
              className="w-full btn-primary-custom py-3 px-6 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
            >
              {t('onboarding.welcome.startTest')}
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={handleSkipTest}
              disabled={isSubmitting}
              className="w-full mt-3 text-muted-foreground py-2 hover:text-foreground disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? 'Laddar...' : t('onboarding.welcome.skip')}
            </button>
          </div>
        )}

        {step === 'notifications' && (
          <div className="bg-card rounded-2xl border border-border p-8 text-center">
            <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-primary/30">
              <Bell className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Aktivera notifikationer
            </h2>
            <p className="text-muted-foreground mb-6">
              Få viktiga meddelanden om bland annat:
            </p>
            <div className="text-left space-y-3 mb-8">
              <div className="flex items-start gap-3 text-sm">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium">VMA-varningar</div>
                  <div className="text-muted-foreground text-xs">Viktiga meddelanden till allmänheten</div>
                </div>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium">Utgångsdatum</div>
                  <div className="text-muted-foreground text-xs">När produkter i förrådet går ut</div>
                </div>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium">Årshjulspåminnelser</div>
                  <div className="text-muted-foreground text-xs">Uppgifter som snart behöver göras</div>
                </div>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium">Familjechatten</div>
                  <div className="text-muted-foreground text-xs">Nya meddelanden från familjen</div>
                </div>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium">Gruppaktivitet</div>
                  <div className="text-muted-foreground text-xs">Nya händelser i beredskapsgrupper</div>
                </div>
              </div>
            </div>

            {!permissionStatus.granted ? (
              <>
                <button
                  onClick={handleEnableNotifications}
                  disabled={isRequestingNotifications}
                  className="w-full py-3 px-6 rounded-lg font-medium transition-all flex items-center justify-center gap-2 mb-3 bg-[#67724e] hover:bg-[#58613f] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRequestingNotifications ? 'Aktiverar...' : 'Aktivera notifikationer'}
                  <Bell className="w-5 h-5" />
                </button>
                <button
                  onClick={handleContinueFromNotifications}
                  className="w-full text-muted-foreground py-2 hover:text-foreground transition-colors text-sm"
                >
                  Hoppa över (du kan aktivera senare)
                </button>
              </>
            ) : (
              <button
                onClick={handleContinueFromNotifications}
                className="w-full btn-primary-custom py-3 px-6 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
              >
                Fortsätt
                <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {step === 'test' && (
          <div className="bg-card rounded-2xl border border-border p-6">
            {/* Progress */}
            <div className="mb-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-muted-foreground">
                  Fråga {currentQuestion + 1} av {quizQuestions.length}
                </span>
                <span className="text-muted-foreground">
                  {quizQuestions[currentQuestion].area}
                </span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${((currentQuestion + 1) / quizQuestions.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Question */}
            {(() => {
              const question = quizQuestions[currentQuestion];
              const Icon = question.icon;
              return (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div 
                      className="rounded-lg p-2"
                      style={{ backgroundColor: `${question.color}20`, color: question.color }}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <h2 className="text-lg font-semibold flex-1">
                      {question.question}
                    </h2>
                  </div>

                  <div className="space-y-3">
                    {question.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleAnswer(option.value)}
                        className={`w-full text-left p-4 rounded-lg border transition-all hover:border-primary hover:bg-primary/5 ${
                          answers[question.id] === option.value
                            ? 'border-primary bg-primary/10'
                            : 'border-border'
                        }`}
                      >
                        <span className="font-medium">{option.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* Navigation */}
                  {currentQuestion > 0 && (
                    <button
                      onClick={handlePrevQuestion}
                      className="mt-6 flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Föregående fråga
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {step === 'analyzing' && (
          <div className="bg-card rounded-2xl border border-border p-8 text-center">
            <div className="animate-pulse">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-primary/30">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">
                {t('onboarding.test.analyzing')}
              </h2>
              <p className="text-muted-foreground">
                {t('onboarding.test.pleaseWait')}
              </p>
            </div>
          </div>
        )}

        {step === 'complete' && (
          <div className="bg-card rounded-2xl border border-border p-8 text-center">
            <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-primary/30">
              <CheckCircle className="w-10 h-10 text-primary" />
            </div>
            
            <h2 className="text-2xl font-bold text-foreground mb-4">
              {t('onboarding.complete.title')}
            </h2>
            
            <div className="mb-6">
              <div className="text-6xl font-bold text-primary mb-2">
                {score}%
              </div>
              <div className={`text-lg font-semibold ${getScoreLevel(score).color}`}>
                {getScoreLevel(score).level}
              </div>
              <p className="text-muted-foreground mt-2">
                {getScoreLevel(score).message}
              </p>
            </div>

            {/* Quick breakdown */}
            <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold mb-3">Snabbanalys</h3>
              <div className="space-y-2 text-sm">
                {score < 50 && (
                  <>
                    <p className="flex items-start gap-2">
                      <Droplets 
                        className="w-4 h-4 mt-0.5 shrink-0" 
                        style={{ color: areaConfig.water.color }}
                      />
                      <span>Börja med vatten – 5 liter per person och dag i en vecka.</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <Zap 
                        className="w-4 h-4 mt-0.5 shrink-0" 
                        style={{ color: areaConfig.energy.color }}
                      />
                      <span>Skaffa pannlampa och powerbank – grundläggande vid strömavbrott.</span>
                    </p>
                  </>
                )}
                {score >= 50 && score < 80 && (
                  <>
                    <p className="flex items-start gap-2">
                      <Heart 
                        className="w-4 h-4 mt-0.5 shrink-0" 
                        style={{ color: areaConfig.health.color }}
                      />
                      <span>Komplettera första hjälpen-väskan och friska upp kunskaperna.</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <Shield 
                        className="w-4 h-4 mt-0.5 shrink-0" 
                        style={{ color: areaConfig.shelter.color }}
                      />
                      <span>Planera för reservvärme och ha en evakueringsplan.</span>
                    </p>
                  </>
                )}
                {score >= 80 && (
                  <p className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span>Fokusera på att hålla förråden uppdaterade och kunskaperna färska.</span>
                  </p>
                )}
              </div>
            </div>

            <button
              onClick={handleComplete}
              disabled={isSubmitting}
              className="w-full btn-primary-custom py-3 px-6 rounded-lg font-medium transition-all disabled:opacity-50"
            >
              {isSubmitting ? 'Laddar...' : t('onboarding.complete.continue')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
